import React, { useEffect, useRef, useState } from 'react';
import { recordEvent } from '../../utils/analytics';
import { blogShareLinks } from '../../utils/blogShare';
import { copyToClipboard } from '../../utils/sharingUrls';

type BlogShareRowProps = {
  slug: string;
  title: string;
  position: 'top' | 'bottom';
};

const BlogShareRow: React.FC<BlogShareRowProps> = ({ slug, title, position }) => {
  const links = blogShareLinks({ slug, title });
  const [notice, setNotice] = useState('');
  const [copyLabel, setCopyLabel] = useState('Copy link');
  const resetTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (resetTimer.current) window.clearTimeout(resetTimer.current);
  }, []);

  const track = (method: string) => {
    recordEvent('share', { method, source: 'blog_post', slug });
  };

  const showNotice = (message: string, label: string) => {
    setNotice(message);
    setCopyLabel(label);
    if (resetTimer.current) window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => {
      setNotice('');
      setCopyLabel('Copy link');
      resetTimer.current = null;
    }, 2000);
  };

  const copyLink = () => {
    copyToClipboard(links.copy).then((ok) => {
      if (!ok) return;
      showNotice('Link copied', 'Link copied');
      track('copy');
    });
  };

  const openInstagram = (url: string, message: string, method: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const href = event.currentTarget.href;
    copyToClipboard(url).then((ok) => {
      if (ok) showNotice(message, 'Copy link');
      track(method);
      window.open(href, '_blank', 'noopener,noreferrer');
    });
  };

  return (
    <nav className={`blog-share blog-share--${position}`} aria-label="Share this post">
      <span className="blog-share-label">Share this post</span>
      <a
        className="blog-share-btn blog-share-whatsapp"
        href={links.whatsapp}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on WhatsApp"
        onClick={() => track('whatsapp')}
      >
        <i className="fab fa-whatsapp" aria-hidden="true" />
        <span>WhatsApp</span>
      </a>
      <a
        className="blog-share-btn blog-share-instagram-post"
        href={links.instagramPost}
        target="_blank"
        rel="noopener noreferrer"
        data-share-url={links.instagramPostText}
        aria-label="Share as an Instagram post"
        onClick={openInstagram(links.instagramPostText, 'Link copied — paste it into your post', 'instagram_post')}
      >
        <i className="fab fa-instagram" aria-hidden="true" />
        <span>Insta post</span>
      </a>
      <a
        className="blog-share-btn blog-share-instagram-story"
        href={links.instagramStory}
        target="_blank"
        rel="noopener noreferrer"
        data-share-url={links.instagramStoryText}
        aria-label="Share as an Instagram story"
        onClick={openInstagram(links.instagramStoryText, 'Link copied — paste it into your story', 'instagram_story')}
      >
        <i className="fab fa-instagram" aria-hidden="true" />
        <span>Insta story</span>
      </a>
      <a
        className="blog-share-btn blog-share-facebook"
        href={links.facebook}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on Facebook"
        onClick={() => track('facebook')}
      >
        <i className="fab fa-facebook-f" aria-hidden="true" />
        <span>Facebook</span>
      </a>
      <a
        className="blog-share-btn blog-share-email"
        href={links.email}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share by email"
        onClick={() => track('email')}
      >
        <i className="fas fa-envelope" aria-hidden="true" />
        <span>Email</span>
      </a>
      <a
        className="blog-share-btn blog-share-linkedin"
        href={links.linkedin}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on LinkedIn"
        onClick={() => track('linkedin')}
      >
        <i className="fab fa-linkedin-in" aria-hidden="true" />
        <span>LinkedIn</span>
      </a>
      <button
        type="button"
        className="blog-share-btn blog-share-copy"
        data-share-url={links.copy}
        aria-label="Copy link to this post"
        onClick={copyLink}
      >
        <i className="fas fa-link" aria-hidden="true" />
        <span>{copyLabel}</span>
      </button>
      <span className="blog-share-status" role="status" aria-live="polite">{notice}</span>
    </nav>
  );
};

export default BlogShareRow;
